import { useEffect, useRef } from 'react'
import { mutate as globalMutate } from 'swr'
import { useAuth } from '@/lib/config/auth-provider'
import { connectWebSocket } from '@/lib/services/websocket'
import { BillingLockedTooltip } from './BillingLockedTooltip'
import { CONTACT_SALES_URL } from '@/pages/SettingsPage/components/billing/planConfig'

/**
 * Paid-first onboarding gate (RND-2610).
 *
 * A non-dismissible full-screen overlay shown when the user's org has the
 * `paidFirstOnboarding` flag set and no active subscription. Self-serve
 * checkout is disabled during the Lemon Squeezy to Paddle migration, so the
 * CTA below is locked and points the user at customer support instead.
 *
 * Unlock is event-driven: the `billing:updated` socket event refreshes the
 * profile once received (e.g. after support activates the subscription by
 * hand).
 *
 * Rendered unconditionally (alongside WelcomeSequence if a new user) since its
 * z-[60] sits above the onboarding overlay's z-50.
 */
export function PaymentFirstGate() {
  const { user, refreshProfile } = useAuth()
  const unlockedRef = useRef(false)

  const isGated =
    user != null &&
    user.featureFlags?.paidFirstOnboarding === true &&
    !user.hasActiveSubscription

  // Subscribe to billing:updated socket event; refresh profile once on receipt.
  useEffect(() => {
    if (!isGated) return

    const ws = connectWebSocket()

    const handleBillingUpdated = () => {
      if (unlockedRef.current) return
      unlockedRef.current = true
      // Re-arm this user's onboarding handoff and force SWR to re-fetch so a
      // same-page unlock transitions directly into onboarding.
      localStorage.setItem('showOnboardingTour', String(user.id))
      void globalMutate('onboarding-state')
      void refreshProfile()
    }

    ws.on('billing:updated', handleBillingUpdated)
    return () => {
      ws.off('billing:updated', handleBillingUpdated)
    }
  }, [isGated, refreshProfile, user?.id])

  if (!isGated) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Payment required to continue"
    >
      <div className="mx-4 max-w-md w-full rounded-2xl border border-border bg-card p-8 shadow-2xl text-center space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">
            Complete your subscription
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Your workspace is ready. Subscribe to unlock full access to
            Fairgen's research tools.
          </p>
        </div>

        {/* Checkout re-wires here once the Paddle integration lands. */}
        <BillingLockedTooltip className="w-full">
          <button
            disabled
            className="w-full rounded-lg bg-primary px-6 py-3 text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Subscribe now
          </button>
        </BillingLockedTooltip>

        <p className="text-xs text-muted-foreground">
          To activate your subscription,{' '}
          <a
            href={CONTACT_SALES_URL}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            contact customer support
          </a>
          .
        </p>
      </div>
    </div>
  )
}
